"use client";

/**
 * The receipt (composer panels, spec 2026-09-19 §2): the composer's receipt
 * row in the shell's chrome plus ONE new element, the Undo chip. The chip
 * posts the run id once and shows the route's words in its place; at
 * `undoUntil` it is gone from the screen (the word and the route keep
 * working for the 24 h window). Panels have no timer; only the chip drains.
 */
import { useEffect, useState } from "react";
import type { ComposerPanel, ComposerUndoResponse } from "@bash/shared";
import { AskShell } from "@/components/task-story/AskShell";
import { UNDO_FAILED } from "@/lib/composer/undo-client";

type Receipt = Extract<ComposerPanel, { kind: "receipt" }>;

function CheckGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9.6" fill="#fff" />
      <path
        className="g-ink"
        fill="none"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7.6 12.3l2.9 2.9 6-6.4"
      />
    </svg>
  );
}

type ChipState = { phase: "idle" | "busy" } | { phase: "said"; words: string };

export function UndoChip({
  runId,
  undoUntil,
  onUndo,
}: {
  runId: string;
  undoUntil: string;
  onUndo: (runId: string) => Promise<ComposerUndoResponse>;
}) {
  const [state, setState] = useState<ChipState>({ phase: "idle" });
  const deadline = Date.parse(undoUntil);
  const [gone, setGone] = useState(() => deadline <= Date.now());
  // ONE setter, the timer's: a deadline already past is the initial state, and
  // a deadline that arrives already past (a new undoUntil on the same chip)
  // drains on the next tick. Nothing sets state synchronously in the effect
  // body, which would cascade a render (react-hooks/set-state-in-effect).
  useEffect(() => {
    const t = setTimeout(
      () => setGone(true),
      Math.max(deadline - Date.now(), 0)
    );
    return () => clearTimeout(t);
  }, [deadline]);
  if (state.phase === "said")
    return (
      <span className="ts-need-wait" data-testid="undo-words">
        {state.words}
      </span>
    );
  if (gone) return null;
  const busy = state.phase === "busy";
  return (
    <button
      type="button"
      className="ts-need-btn"
      data-testid="undo-chip"
      disabled={busy}
      onClick={() => {
        if (busy) return;
        setState({ phase: "busy" });
        void onUndo(runId)
          .then((r) => setState({ phase: "said", words: r.words }))
          .catch(() => setState({ phase: "said", words: UNDO_FAILED }));
      }}
    >
      Undo
    </button>
  );
}

export function ReceiptPanel({
  panel,
  onClose,
  onUndo,
}: {
  panel: Receipt;
  onClose: () => void;
  onUndo: (runId: string) => Promise<ComposerUndoResponse>;
}) {
  return (
    <AskShell
      kind="receipt"
      tile={<CheckGlyph />}
      title={panel.title}
      why={panel.meta}
      onClose={onClose}
    >
      {panel.runId && panel.undoUntil && (
        <div className="ts-need-acts">
          {/* Keyed by the run it takes back: a host that draws a card straight,
              without the deck's own per-receipt key, still mounts a FRESH chip
              for a later receipt instead of showing the earlier run's words. */}
          <UndoChip
            key={`${panel.runId}:${panel.undoUntil}`}
            runId={panel.runId}
            undoUntil={panel.undoUntil}
            onUndo={onUndo}
          />
        </div>
      )}
    </AskShell>
  );
}
